import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Divider, Modal, Row } from 'antd';
import styled from 'styled-components';
import { RemoveScroll } from 'react-remove-scroll';
import { useNavigate } from 'react-router';
import { ReactComponent as ConfirmIcon } from '../../../layout/icons/green-circle-with-checkmark.svg';
import { ReactComponent as DownloadGreenIcon } from '../../../layout/icons/download-green.svg';
import { Typography } from '../../../layout/components/Typography';
import { ColorScheme } from '../../../layout/colorScheme';
import { PrimaryButton } from '../../../common-components/Buttons';
import useIsMobileView from '../../../hooks/useIsMobileView';
import CMSStore from '../../../stores/content-stores/CMSStore';
import BalanceStore from '../../../stores/BalanceStore';
import PaymentCardStore from '../../../stores/PaymentCardStore';
import { PaymentCardStatus } from '../../../swagger/api';
import RouteUrlPaths from '../../../RouteUrlPaths';
import decimalFormatting from '../../../utils/decimalFormatting';

const StyledModal = styled(Modal)<{
  ismobileview: boolean;
  mobilebuttondivheight: number;
}>`
  text-align: center !important;
  padding-bottom: 0;
  ${(props) => props.ismobileview && 'top: 0%'};
  ${(props) => props.ismobileview && 'margin: 0%'};

  .ant-modal-content {
    ${(props) =>
      !props.ismobileview
        ? 'display: inline-flex; padding: 53px 10% 6% 10%'
        : 'min-height: 100vh; width: 100vw'};
    border-radius: ${(props) => (props.ismobileview ? '0px' : '20px')};
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.2);
    .ant-modal-body {
      ${(props) => (props.ismobileview ? 'padding-top: 20%; padding-inline: 10%' : 'padding: 0px')};
      padding-bottom: ${(props) => props.ismobileview && `${props.mobilebuttondivheight}px`};
    }
  }

  .ant-modal-close {
    margin: 2% 2% 0 0;
    cursor: pointer;

    .ant-modal-close-x {
      color: ${ColorScheme.BrobizzGreen};
      svg {
        height: auto;
        width: 24px;
      }
    }
  }
`;

const TopIconRow = styled(Row)`
  margin-bottom: 28px;
`;

const MobileIcon = styled(ConfirmIcon)`
  width: 56px;
  height: 56px;
`;

const ContentRow = styled(Row)`
  text-align: center;
  margin: 2% 0 15px 0;
`;

const WarningRow = styled(Row)`
  text-align: center;
  margin: 0 0 25px 0;
`;

const ButtonRow = styled(Row)`
  margin: 0 0 5% 0;
`;

const LinkRow = styled(Row)`
  margin-top: 16px;
`;

const MobileViewButton = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  padding: 14px 0;
  background-color: #fff;
  z-index: 1070;
  box-shadow: 0px 2px 14px rgba(0, 0, 0, 0.153);
`;

const SettlementBodyEmphasis = styled.span`
  font-family: 'Campton Medium', sans-serif;
  -webkit-font-smoothing: subpixel-antialiased;
  -moz-osx-font-smoothing: auto;
`;

const DownloadLinkIcon = styled(DownloadGreenIcon)`
  flex-shrink: 0;
  display: block;
  width: 24px;
  height: 24px;
`;

const DownloadLinkLabel = styled.span`
  line-height: 1;
  @supports (text-box-trim: trim-both) {
    text-box-trim: trim-both;
    text-box-edge: cap alphabetic;
  }
`;

function renderSettlementBodyContent(
  bodyTemplate: string | null | undefined,
  amountStr: string,
  currency: string
): React.ReactNode {
  if (!bodyTemplate) return undefined;
  const parts = bodyTemplate.split(/(\{amount\}|\{currency\})/);
  /* eslint-disable react/no-array-index-key -- CMS template segments keep stable order */
  const nodes = parts.map((part, i) => {
    if (part === '{amount}') {
      return <SettlementBodyEmphasis key={i}>{amountStr}</SettlementBodyEmphasis>;
    }
    if (part === '{currency}') {
      return <SettlementBodyEmphasis key={i}>{currency}</SettlementBodyEmphasis>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
  /* eslint-enable react/no-array-index-key */
  return nodes;
}

interface BalanceSettlementModalProps {
  cmsStore: CMSStore;
  balanceStore: BalanceStore;
  paymentCardStore: PaymentCardStore;
  onDownloadReceipt?: () => void;
  onClose: () => void;
}

const BalanceSettlementModal: FC<BalanceSettlementModalProps> = ({
  cmsStore,
  balanceStore,
  paymentCardStore,
  onDownloadReceipt,
  onClose
}) => {
  const { balance, settlementStatus, settledAmount, settledCurrency, settledBalanceType } =
    balanceStore;
  const balanceAmount = settledAmount ?? balance?.totalBalance ?? 0;
  const currency = (settledCurrency ?? balance?.currency ?? 'DKK').toUpperCase();
  const isPayout = (settledBalanceType ?? balance?.balanceType) === 'Negative';
  const isPartiallySettled = settlementStatus?.status === 'PartiallySettled';

  const isMobileView = useIsMobileView();
  const navigate = useNavigate();
  const refDiv: any = useRef(null);
  const [mobileButtonHeight, setMobileButtonHeight] = useState(0);

  const paymentCard = paymentCardStore.paymentMethods[0];
  const isCardBlocked =
    paymentCard?.paymentCardStatus === PaymentCardStatus.BlockedCard ||
    paymentCard?.paymentCardStatus === PaymentCardStatus.ExpiredCard;

  useEffect(() => {
    const currentAccountId = cmsStore.rootStore.userStore.currentAccount?.id;
    if (currentAccountId && paymentCardStore.paymentMethods.length === 0) {
      paymentCardStore.getPaymentMethodsByAccountId();
    }
  }, [cmsStore.rootStore.userStore.currentAccount?.id, paymentCardStore]);

  useLayoutEffect(() => {
    if (isMobileView && refDiv.current) {
      const mobileButtonDivHeight = refDiv.current.clientHeight;
      if (mobileButtonDivHeight) {
        setMobileButtonHeight(mobileButtonDivHeight);
      }
    }
  }, [isMobileView]);

  const successModalKey = isPayout
    ? 'SettlementSuccessModalPayout'
    : 'SettlementSuccessModalCharge';
  const successModalContent = cmsStore.getContent(successModalKey);
  const header = successModalContent?.content;
  const bodyTemplate = successModalContent?.subText;
  const displayBalanceAmount = balanceAmount < 0 ? Math.abs(balanceAmount) : balanceAmount;
  const amountStr = decimalFormatting(displayBalanceAmount);
  const contentBody = renderSettlementBodyContent(bodyTemplate, amountStr, currency);

  const downloadReceiptLinkKey = isPayout ? 'DownloadReceiptLinkPayout' : 'DownloadReceiptLink';
  const downloadLinkText = cmsStore.getContent(downloadReceiptLinkKey)?.content;

  const partiallySettledWarningKey = isPayout
    ? 'SettlementPartiallySettledWarningPayout'
    : 'SettlementPartiallySettledWarningCharge';
  const partiallySettledWarningText = isPartiallySettled
    ? cmsStore.getContent(partiallySettledWarningKey)?.content
    : null;

  const cardBlockedWarningText =
    !isPartiallySettled && isCardBlocked
      ? cmsStore.getContent('PaymentCardBlockedWarning')?.content
      : null;

  const warningText = partiallySettledWarningText ?? cardBlockedWarningText ?? null;

  const primaryButtonText = isPartiallySettled
    ? cmsStore.getContent('CloseLink')?.content
    : isCardBlocked
    ? cmsStore.getContent('UpdatePaymentCardButton')?.content
    : cmsStore.getContent('Ok')?.content;

  const closeLinkText = cmsStore.getContent('CloseLink')?.content;

  const handlePrimaryButtonClick = () => {
    if (isPartiallySettled) {
      onClose();
    } else if (isCardBlocked) {
      onClose();
      navigate(`/${RouteUrlPaths.PaymentCard}`);
    } else {
      onClose();
    }
  };

  const handleDownloadReceipt = async () => {
    if (onDownloadReceipt) {
      onDownloadReceipt();
    } else {
      const customerId = cmsStore.rootStore.userStore.currentAccount?.id;
      if (customerId) {
        try {
          await balanceStore.getBalanceSettlementPdf(customerId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to download receipt:', error);
        }
      }
    }
  };

  const topIcon = isMobileView ? <MobileIcon /> : <ConfirmIcon />;
  const topIconRow = <TopIconRow justify="center">{topIcon}</TopIconRow>;

  const headerRow = (
    <Row justify="center" style={{ marginBottom: '2%' }}>
      <Typography variant="h6" as="h2" color={ColorScheme.DarkGreen} align="center">
        {header}
      </Typography>
    </Row>
  );

  const contentRow = (
    <ContentRow justify="center">
      <Typography variant="contentSmall" as="p" color={ColorScheme.DarkGreen} align="center">
        {contentBody}
      </Typography>
    </ContentRow>
  );

  const downloadLinkRow = (
    <Row justify="center" style={{ marginBottom: warningText ? '0px' : '37px' }}>
      <Typography
        variant="contentLink"
        as="a"
        color={ColorScheme.BrobizzGreen}
        align="left"
        onClick={handleDownloadReceipt}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: isMobileView ? '8px' : '14px'
        }}
      >
        <DownloadLinkIcon aria-hidden />
        <DownloadLinkLabel>{downloadLinkText}</DownloadLinkLabel>
      </Typography>
    </Row>
  );

  const warningRow = warningText && (
    <WarningRow justify="center">
      <Typography
        variant="contentSmall"
        as="p"
        color={ColorScheme.DarkGreen}
        align="center"
        style={{
          color: isPartiallySettled ? ColorScheme.DarkGreen : '#ff004a',
          whiteSpace: isPartiallySettled ? 'pre-line' : undefined
        }}
      >
        {warningText}
      </Typography>
    </WarningRow>
  );

  const primaryButton = (
    <PrimaryButton onClick={handlePrimaryButtonClick}>{primaryButtonText}</PrimaryButton>
  );

  const primaryButtonRow = <ButtonRow justify="center">{primaryButton}</ButtonRow>;

  const closeLinkRow = !isPartiallySettled && isCardBlocked && (
    <LinkRow justify="center">
      <Typography
        variant="contentSmallLink"
        as="a"
        color={ColorScheme.BrobizzGreen}
        align="center"
        onClick={onClose}
        style={{ cursor: 'pointer' }}
      >
        {closeLinkText}
      </Typography>
    </LinkRow>
  );

  const mobileViewButtons = isMobileView && (
    <MobileViewButton ref={refDiv}>
      <Row justify="center">{primaryButton}</Row>
      {!isPartiallySettled && isCardBlocked && (
        <LinkRow justify="center" style={{ marginTop: '16px' }}>
          <Typography
            variant="contentSmallLink"
            as="a"
            color={ColorScheme.BrobizzGreen}
            align="center"
            onClick={onClose}
            style={{ cursor: 'pointer' }}
          >
            {closeLinkText}
          </Typography>
        </LinkRow>
      )}
    </MobileViewButton>
  );

  return (
    <RemoveScroll>
      <StyledModal
        visible
        footer={null}
        maskClosable={false}
        centered={!isMobileView}
        keyboard={false}
        closable
        onCancel={onClose}
        ismobileview={isMobileView}
        mobilebuttondivheight={mobileButtonHeight}
      >
        {topIconRow}
        {headerRow}
        {contentRow}
        {downloadLinkRow}
        {warningText && <Divider style={{ margin: '25px 0' }} />}
        {warningRow}
        {isMobileView ? (
          mobileViewButtons
        ) : (
          <>
            {primaryButtonRow}
            {closeLinkRow}
          </>
        )}
      </StyledModal>
    </RemoveScroll>
  );
};

BalanceSettlementModal.defaultProps = {
  onDownloadReceipt: undefined
};

const ObservedBalanceSettlementModal = observer(BalanceSettlementModal);
export default ObservedBalanceSettlementModal;
